<script setup>
import { useWebSpeechRecognition } from '@core/composable/useWebSpeechRecognition.js'
import { uiid } from '@helpers/IdHelpler.js'

const props = defineProps({
  modelValue: null,
  voiceEnabled: {
    type: Boolean,
    default: true,
  },
  cameraEnabled: {
    type: Boolean,
    default: true,
  },

  // New speech recognition props with backward compatibility
  appendMode: {
    type: Boolean,
    // Keep adding recognized speech to the end
    default: true,
  },
  language: {
    type: String,
    default: 'vi-VN',
  },
  speechTimeout: {
    type: Number,
    // Silence timeout: stop after 10s of inactivity
    default: 10000,
  },
  showNotifications: {
    type: Boolean,
    default: true,
  },
  continuousMode: {
    type: Boolean,
    default: true,
  },
  interimResults: {
    type: Boolean,
    default: true,
  },

  // Smart punctuation based on pause duration
  autoPunctuation: {
    type: Boolean,
    default: true,
  },
  pauseCommaMs: {
    type: Number,
    default: 1500,
  },
  pausePeriodMs: {
    type: Number,
    default: 3000,
  },

  // Visual feedback props
  showRecordingIndicator: {
    type: Boolean,
    default: true,
  },
  pulseAnimation: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:model-value', 'speech-start', 'speech-end', 'speech-result', 'speech-error'])

defineOptions({
  name: 'AppTextArea',
  inheritAttrs: false,
})

// Component state
const value = ref(props.modelValue)
const attrs = useAttrs()
const elementId = `text-area-${uiid()}`
const label = computed(() => attrs.label ?? "")

// Toast notifications
const { showError, showSuccess, showWarning } = useToast()

// Camera dialog integration (existing functionality)
const dialog = props.cameraEnabled ? inject('camera-translate') : null

// Speech recognition setup with new implementation
const speechRecognition = useWebSpeechRecognition({
  language: props.language,
  continuous: props.continuousMode,
  interimResults: props.interimResults,
  appendMode: props.appendMode,
  timeout: props.speechTimeout,
})

const {
  isSupported,
  isListening,
  isProcessing,
  transcript,
  error,
  start: startRecognition,
  stop: stopRecognition,
  toggle: toggleRecognition,
  reset: resetRecognition,
} = speechRecognition

// Track last transcript and time to append only new increments and detect pauses
const lastTranscript = ref('')
const lastUpdateAt = ref(0)

// Computed properties for visual states
// Show textarea's loading indicator while processing or listening
const loading = computed(() => attrs.loading || isProcessing.value || isListening.value)

const microphoneIcon = computed(() => {
  // Show off icon for any error or when browser not supported
  if (!isSupported.value || error.value) return 'tabler-microphone-off'
  if (isListening.value) return 'tabler-microphone-filled'
  if (isProcessing.value) return 'tabler-loader'
  return 'tabler-microphone'
})

const microphoneColor = computed(() => {
  if (!isSupported.value || error.value) return 'error'
  if (isListening.value) return 'success'
  if (isProcessing.value) return 'warning'
  return 'default'
})

const microphoneTooltip = computed(() => {
  if (!isSupported.value) return 'Trình duyệt không hỗ trợ nhận dạng giọng nói'
  if (error.value) return `Lỗi: ${error.value}`
  if (isListening.value) return 'Đang nghe... (Click để dừng)'
  if (isProcessing.value) return 'Đang xử lý...'
  
  return 'Click để bắt đầu nhận dạng giọng nói'
})

// Speech recognition handlers
const handleSpeechToggle = async () => {
  if (!isSupported.value) {
    const errorMsg = 'Trình duyệt không hỗ trợ nhận dạng giọng nói'
    if (props.showNotifications) {
      showError(errorMsg)
    }
    emit('speech-error', errorMsg)
    
    return
  }

  try {
    if (isListening.value) {
      stopRecognition()
      emit('speech-end')
      if (props.showNotifications) {
        showWarning('Đã dừng nhận dạng giọng nói')
      }
    } else {
      await startRecognition()
      emit('speech-start')
      if (props.showNotifications) {
        showSuccess('Bắt đầu nhận dạng giọng nói')
      }
    }
  } catch (err) {
    const errorMsg = 'Không thể khởi động nhận dạng giọng nói'
    if (props.showNotifications) {
      showError(errorMsg)
    }
    emit('speech-error', errorMsg)
  }
}

// Camera dialog handler (existing functionality)
const handleShowDialog = () => {
  dialog.show = true
  dialog.id = elementId
}

// Watchers
watch(transcript, newTranscript => {
  if (!newTranscript) return

  const now = Date.now()
  const prevFull = lastTranscript.value || ''
  const prevValue = value.value || ''
  let toAppend = ''

  // Determine only the new suffix from the recognizer
  if (newTranscript.startsWith(prevFull)) {
    toAppend = newTranscript.slice(prevFull.length)
  } else if (prevFull.startsWith(newTranscript)) {
    // Recognizer shortened text (restart/correction); don't append dup
    toAppend = ''
  } else {
    let i = 0
    const minLen = Math.min(prevFull.length, newTranscript.length)
    while (i < minLen && prevFull[i] === newTranscript[i]) i++
    toAppend = newTranscript.slice(i)
  }

  if (props.appendMode) {
    if (toAppend) {
      const dt = lastUpdateAt.value ? (now - lastUpdateAt.value) : 0
      const trimmedPrev = prevValue.replace(/\s+$/u, '')

      let punct = ''
      const shouldConsiderPunct = props.autoPunctuation && trimmedPrev.length > 0
      const alreadyPunctuated = /[.,;:!?]$/u.test(trimmedPrev) || /^[.,;:!?]/u.test(toAppend)

      if (shouldConsiderPunct && !alreadyPunctuated) {
        if (dt >= props.pausePeriodMs) punct = '.'
        else if (dt >= props.pauseCommaMs) punct = ','
      }

      // Capitalize first alphabet char if after a period
      let segment = toAppend
      const endsWithPeriod = punct === '.' || /\.(\s*)$/u.test(trimmedPrev)
      if (endsWithPeriod) {
        segment = segment.replace(/(^\s*)([\p{L}])/u, (m, sp, ch) => sp + ch.toUpperCase())
      }

      if (punct) {
        value.value = `${trimmedPrev}${punct} ${segment}`
      } else {
        // If previous ends with period but has no trailing space, add a space
        if (/\.$/u.test(prevValue)) {
          value.value = `${prevValue} ${segment}`
        } else {
          value.value = prevValue + segment
        }
      }
    }
    lastTranscript.value = newTranscript
  } else {
    value.value = newTranscript
    lastTranscript.value = newTranscript
  }

  lastUpdateAt.value = now
  emit('speech-result', newTranscript)
})

watch(error, newError => {
  if (newError) {
    if (props.showNotifications) {
      showError(newError)
    }
    emit('speech-error', newError)
  }
})

watch(() => dialog?.content, content => {
  if (dialog && dialog.id === elementId) {
    value.value = content
  }
})

watch(value, newValue => {
  emit('update:model-value', newValue)
})

watch(() => props.modelValue, v => {
  value.value = v
})

// Cleanup on unmount
onUnmounted(() => {
  stopRecognition()
})

const speech = ref()
</script>

<template>
  <VLabel
    v-if="$attrs.label"
    :for="elementId"
    class="text-body-2 text-high-emphasis w-100"
    :style="attrs.labelStyle"
    :text="$attrs.label"
  />
  <div class="speech-field-wrapper" :class="{ 'speech-field-wrapper--listening': isListening }">
    <VTextarea
      v-bind="{
        ...$attrs,
        label: undefined,
        id: elementId,
      }"
      persistent-placeholder
      v-model="value"
      :loading="loading"
      :class="{
        'speech-field--listening': isListening,
        'speech-field--processing': isProcessing,
        'speech-field--error': !!error,
      }"
    >
    <template #append-inner>
      <IconBtn
        v-if="cameraEnabled"
        size="26"
        @click="handleShowDialog"
      >
        <VIcon
          size="16"
          icon="tabler-camera"
        />
      </IconBtn>
      <IconBtn
        v-if="voiceEnabled"
        size="26"
        class="voice-btn"
        :class="{
          'voice-btn--recording': isListening && pulseAnimation,
          'voice-btn--processing': isProcessing,
          'voice-btn--error': !!error,
        }"
        :color="microphoneColor"
        :title="microphoneTooltip"
        :disabled="!isSupported"
        :aria-pressed="isListening"
        @click="handleSpeechToggle"
      >
        <VIcon
          size="16"
          :icon="microphoneIcon"
          :class="{ 'spin-icon': isProcessing }"
        />

        <!-- Recording indicator dot -->
        <div
          v-if="isListening && showRecordingIndicator"
          class="recording-indicator"
        />
      </IconBtn>
    </template>
    <template
      v-for="(_, name) in $slots"
      #[name]="slotProps"
    >
      <slot
        :name="name"
        v-bind="slotProps || {}"
      />
    </template>
    </VTextarea>

    <!-- Status indicator overlay for clearer visibility -->
<!--    <Transition name="status-fade">-->
<!--      <div-->
<!--        v-if="isListening || isProcessing"-->
<!--        class="status-overlay"-->
<!--        :class="{-->
<!--          'status-overlay&#45;&#45;listening': isListening,-->
<!--          'status-overlay&#45;&#45;processing': isProcessing,-->
<!--        }"-->
<!--      >-->
<!--        <VChip-->
<!--          size="small"-->
<!--          :color="isListening ? 'success' : 'warning'"-->
<!--          variant="flat"-->
<!--          class="status-chip"-->
<!--        >-->
<!--          <VIcon-->
<!--            :icon="isListening ? 'tabler-microphone' : 'tabler-loader'"-->
<!--            size="12"-->
<!--            class="me-1"-->
<!--            :class="{ 'spin-icon': isProcessing }"-->
<!--          />-->
<!--          {{ isListening ? 'Đang nghe...' : 'Đang xử lý...' }}-->
<!--        </VChip>-->
<!--      </div>-->
<!--    </Transition>-->
  </div>
</template>

<style scoped>
.voice-btn {
  position: relative;
  transition: all 0.3s ease;
}

.voice-btn--recording {
  animation: pulse 1.5s infinite;
}

.voice-btn--processing .spin-icon {
  animation: spin 1s linear infinite;
}

.voice-btn--error {
  color: rgb(var(--v-theme-error)) !important;
}

.recording-indicator {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  background-color: rgb(var(--v-theme-error));
  border-radius: 50%;
  animation: blink 1s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes blink {
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
}
</style>

<style scoped>
/* Wrapper and field state styles to make loading obvious */
.speech-field-wrapper {
  position: relative;
  transition: all 0.3s ease;
}

.speech-field-wrapper--listening {
  filter: drop-shadow(0 0 8px rgba(var(--v-theme-success), 0.3));
}

.speech-field--listening :deep(.v-field__outline) {
  border-color: rgb(var(--v-theme-success)) !important;
  border-width: 2px !important;
}

.speech-field--processing :deep(.v-field__outline) {
  border-color: rgb(var(--v-theme-warning)) !important;
  border-width: 2px !important;
}

.speech-field--error :deep(.v-field__outline) {
  border-color: rgb(var(--v-theme-error)) !important;
  border-width: 2px !important;
}

.status-overlay {
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  pointer-events: none;
}

.status-chip {
  font-size: 0.75rem !important;
  height: 24px !important;
}

.status-fade-enter-active,
.status-fade-leave-active {
  transition: all 0.3s ease;
}

.status-fade-enter-from,
.status-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}
</style>
